// Composer-local ATOMS: the configuration chip (the prototype wk-cchip in its key/gated/goal/dead
// tones), the field label, the muted why-line with its mono accent, the shield glyph the operator
// chrome carries, and the two governed button recipes. Tokens only (G-181); a chip's variant is an
// INPUT derived from projection data by the caller (G-182), never decided here. Machine hooks ride
// data attributes (data-chip / data-variant) so tests assert behaviour, not copy (G-180).

import type { ReactNode } from 'react';

/** The one primary action per view (house recipe — MetricGateActions/GateRow). */
export const PRIMARY_BUTTON_CLASS =
  'rounded-sm bg-action px-3 py-1.5 text-sm font-medium text-action-text hover:bg-action-hover disabled:opacity-60';

/** The quiet secondary action (house ghost recipe). */
export const GHOST_BUTTON_CLASS =
  'rounded-sm border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:border-border-strong disabled:opacity-60';

/** The chip tones the composer renders: key (subject key), gated (amber), goal/dead exits, plain. */
export type ConfigChipVariant = 'plain' | 'key' | 'gated' | 'goal' | 'dead';

const CHIP_VARIANT_CLASS: Readonly<Record<ConfigChipVariant, string>> = {
  plain: 'border-border bg-surface text-text',
  key: 'border-sage bg-forest-tint text-forest',
  gated: 'border-bracken bg-bracken-tint text-bracken',
  goal: 'border-sage bg-forest-tint text-forest',
  dead: 'border-dashed border-border bg-surface text-text-subtle',
};

export interface ConfigChipProps {
  /** The data-chip hook naming what kind of config value this chip carries (e.g. `binding`). */
  readonly hook: string;
  readonly variant?: ConfigChipVariant;
  readonly children: ReactNode;
}

/** One configuration chip (prototype wk-cchip) — mono, radius-sm, variant-toned. */
export function ConfigChip({
  hook,
  variant = 'plain',
  children,
}: ConfigChipProps): React.JSX.Element {
  return (
    <span
      data-chip={hook}
      data-variant={variant}
      className={`ritona-numeric inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1 text-xs ${CHIP_VARIANT_CLASS[variant]}`}
    >
      {children}
    </span>
  );
}

/** An uppercased mono field label; associates with a control when `htmlFor` is given. */
export function FieldLabel({
  htmlFor,
  children,
}: {
  readonly htmlFor?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  const className = 'ritona-numeric block text-xs uppercase tracking-eyebrow text-text-subtle';
  if (htmlFor !== undefined) {
    return (
      <label htmlFor={htmlFor} className={className}>
        {children}
      </label>
    );
  }
  return <span className={className}>{children}</span>;
}

/** The muted rationale line under a step body (prototype wk-cmp-why). */
export function WhyLine({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <p className="mt-3 max-w-prose text-sm leading-relaxed text-text-muted">{children}</p>;
}

/** The why-line's emphasised fragment — mono Forest, matching the prototype's accent. */
export function Accent({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <em className="ritona-numeric text-xs not-italic text-forest">{children}</em>;
}

/** The operator-gate shield glyph (Icon.tsx idiom: 24×24 inline stroke, 1.7 weight, currentColor). */
export function ShieldIcon({ size = 12 }: { readonly size?: number }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3.5l7 2.5v5c0 4.2-2.9 7.4-7 8.5-4.1-1.1-7-4.3-7-8.5V6z" />
    </svg>
  );
}
